import { pieceClassement } from '@admin-pmvb/core'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { api, corps, ErreurApi, type FichePiece, type FicheTache, lire, requetes } from '../api'
import { ClasserPiece } from './ClasserPiece'
import { Icone } from './Icone'

// Fil d'Ariane Drive d'une pièce, toujours en tête de sa fiche et de sa tâche : où elle est
// rangée ; sinon où elle ira (dossier choisi, ou proposé par l'assistant et à valider) ;
// sinon un dossier proposé d'après la rubrique lue, à confirmer d'un geste ; sinon ce qui
// manque pour le savoir, avec le choix du dossier. Le chemin vient du serveur (règle pure
// `filDrive`, la même que le rangement) ; chaque dossier déjà dans le Drive s'ouvre d'un clic.

type Fil = FichePiece['filDrive']

const LIBELLES: Record<Fil['etat'], string> = {
  range: 'Drive',
  prevu: 'Sera rangée dans',
  propose: 'Dossier proposé par l’assistant',
  suggere: 'Proposé d’après sa rubrique',
  a_choisir: 'Dossier à choisir',
  sans_chemin: 'Emplacement Drive inconnu',
  sans_entite: 'Pas encore dans le Drive',
}

export function FilDrivePiece({
  pieceId,
  ecriture,
  nom,
  sansActions = false,
}: {
  pieceId: string
  ecriture: boolean
  /** Nom de la pièce, quand la tâche en a plusieurs. */
  nom?: string | null
  /** La décision de la tâche classe déjà la pièce : l'emplacement seul, sans geste en double. */
  sansActions?: boolean
}) {
  const client = useQueryClient()
  const fiche = useQuery({
    ...requetes.piece(pieceId),
    // Placement ou rangement en cours côté worker : le fil se met à jour seul.
    refetchInterval: (q) => (q.state.data?.rangement === 'a_faire' ? 5_000 : false),
  })
  const [choisir, setChoisir] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const p = fiche.data
  const chemin = p?.filDrive.dossiers.join('/') ?? ''
  // Chaque dossier du chemin, ouvrable dans le Drive ; relu quand le chemin change.
  const liens = useQuery({
    ...requetes.filDrive(pieceId),
    queryKey: [...requetes.filDrive(pieceId).queryKey, chemin],
    enabled: chemin !== '',
    staleTime: 10 * 60_000,
    retry: false,
  })
  if (!p) return null
  const f = p.filDrive
  const peutClasser = ecriture && p.entiteId !== null && !sansActions

  const agir = async (action: () => Promise<unknown>) => {
    setErreur(null)
    try {
      await action()
      await client.invalidateQueries()
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : 'Action impossible.')
    }
  }
  const classerIci = (dossierId: string) =>
    agir(() =>
      lire(
        api.pieces[':id'].classer.$post({
          param: { id: pieceId },
          json: corps(pieceClassement, { dossierId }),
        }),
      ),
    )
  const validerDossier = (dossierId: string) =>
    agir(() => lire(api.dossiers[':id'].valider.$post({ param: { id: dossierId } })))

  const autre = (libelle: string) =>
    peutClasser &&
    !choisir && (
      <button type="button" className="bouton-discret" onClick={() => setChoisir(true)}>
        {libelle}
      </button>
    )

  return (
    <nav className={`fil-drive fil-drive-${f.etat}`} aria-label="Emplacement dans le Drive">
      <div className="fil-drive-ligne">
        <Icone nom={f.etat === 'range' ? 'folder' : 'drive_file_move'} taille={18} />
        <span className="fil-drive-etat">{LIBELLES[f.etat]}</span>
        {nom && <span className="fil-drive-nom">{nom}</span>}
        {(f.dossiers.length > 0 || f.fichier) && (
          <ol className="fil-drive-chemin">
            {f.dossiers.map((d, i) => {
              const lien = liens.data?.[i]?.nom === d ? liens.data[i]!.lien : null
              return (
                <li key={i}>
                  {lien ? (
                    <a
                      href={lien}
                      target="_blank"
                      rel="noreferrer"
                      title={`Ouvrir « ${d} » dans le Drive`}
                    >
                      {d}
                    </a>
                  ) : (
                    d
                  )}
                </li>
              )
            })}
            {f.fichier && (
              <li className="fil-drive-fichier">
                {f.lien ? (
                  <a href={f.lien} target="_blank" rel="noreferrer">
                    {f.fichier}
                  </a>
                ) : (
                  f.fichier
                )}
              </li>
            )}
          </ol>
        )}
        <span className="fil-drive-actions">
          {f.etat === 'range' && (
            <>
              {f.lien && (
                <a href={f.lien} target="_blank" rel="noreferrer" className="bouton-discret">
                  <Icone nom="open_in_new" taille={16} /> Ouvrir
                </a>
              )}
              {autre('Reclasser')}
            </>
          )}
          {f.etat === 'prevu' && autre('Changer de dossier')}
          {f.etat === 'propose' && f.dossierId && (
            <>
              {ecriture && !sansActions && (
                <button
                  type="button"
                  className="bouton petit"
                  onClick={() => void validerDossier(f.dossierId!)}
                >
                  Valider le dossier
                </button>
              )}
              {autre('Classer ailleurs')}
            </>
          )}
          {f.etat === 'suggere' && f.dossierId && (
            <>
              {peutClasser && (
                <button
                  type="button"
                  className="bouton petit"
                  onClick={() => void classerIci(f.dossierId!)}
                >
                  Classer ici
                </button>
              )}
              {autre('Autre dossier')}
            </>
          )}
          {f.etat === 'a_choisir' && autre('Choisir le dossier')}
          {f.etat === 'sans_chemin' && p.entiteId && (
            <Link to="/entites/$id" params={{ id: p.entiteId }} className="bouton-discret">
              Régler sur la fiche de l’entité
            </Link>
          )}
        </span>
      </div>
      {f.motif && <p className="fil-drive-motif">{f.motif}</p>}
      {erreur && <p className="alerte">{erreur}</p>}
      {choisir && (
        <div className="fil-drive-choix">
          <ClasserPiece
            p={p}
            ecriture={ecriture}
            ouvertAuDepart
            onFermer={() => setChoisir(false)}
          />
        </div>
      )}
    </nav>
  )
}

/** En tête d'une tâche : le fil de chacune de ses pièces (sauf rejetées). */
export function FilsDriveTache({
  t,
  ecriture,
  sansActions = false,
}: {
  t: FicheTache
  ecriture: boolean
  sansActions?: boolean
}) {
  const [tout, setTout] = useState(false)
  const pieces = t.pieces.filter((p) => p.statut !== 'ignoree')
  if (pieces.length === 0) return null
  const MAX = 3
  const vues = tout ? pieces : pieces.slice(0, MAX)
  return (
    <div className="fils-drive">
      {vues.map((p) => (
        <FilDrivePiece
          key={p.id}
          pieceId={p.id}
          ecriture={ecriture}
          sansActions={sansActions}
          nom={pieces.length > 1 ? (p.nomFichier ?? p.emetteur ?? 'Pièce') : null}
        />
      ))}
      {pieces.length > MAX && (
        <button type="button" className="bouton-discret" onClick={() => setTout(!tout)}>
          {tout ? 'Moins de pièces' : `Voir les ${pieces.length - MAX} autres pièces`}
        </button>
      )}
    </div>
  )
}
